import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

import { useTabNavigation } from './hooks/useTabNavigation'

interface TabNavigationProps {
  items: string[]
  selectedItem?: Nullable<string>
  counts?: number[]
  onItemSelect: (item: string) => void
  getItemLabel?: (item: string) => string
  className?: string
}

export function TabNavigation({
  items,
  selectedItem,
  counts,
  onItemSelect,
  getItemLabel,
  className,
}: TabNavigationProps) {
  const {
    containerRef,
    measureButtonsRef,
    visibleButtonsContainerRef,
    arrowButtonsRef,
    visibleItems,
    showLeftArrow,
    showRightArrow,
    handleNavigate,
  } = useTabNavigation(items)

  return (
    <>
      <nav
        ref={containerRef}
        className={cn(
          'flex items-center gap-4 min-w-0 overflow-x-hidden',
          className
        )}
        style={{ width: '100%', boxSizing: 'border-box' }}
        aria-label='Tab navigation'
      >
        <div
          ref={visibleButtonsContainerRef}
          className='flex gap-4 pb-2 overflow-hidden'
          style={{ flex: '1 1 0%', minWidth: 0, boxSizing: 'border-box' }}
          role='tablist'
        >
          {visibleItems.map(item => {
            const itemIndex = items.indexOf(item)
            const count =
              counts && itemIndex >= 0 ? counts[itemIndex] : undefined
            const label = getItemLabel ? getItemLabel(item) : item
            return (
              <Button
                key={item}
                type='button'
                variant='ghost'
                onClick={() => onItemSelect(item)}
                className={cn(
                  'px-4 py-2 whitespace-nowrap font-medium text-sm transition-colors h-auto rounded-none shrink-0',
                  selectedItem === item
                    ? 'text-red-600 border-b-2 border-red-600 border-x-0 border-t-0'
                    : 'text-gray-600 hover:text-gray-900'
                )}
                role='tab'
                aria-selected={selectedItem === item}
              >
                {label}
                {count !== undefined && (
                  <span className='text-xs text-gray-500 ml-2 bg-gray-100 border rounded-full p-1'>
                    {count}
                  </span>
                )}
              </Button>
            )
          })}
        </div>

        <Button
          type='button'
          variant='ghost'
          onClick={() => handleNavigate('left')}
          disabled={!showLeftArrow}
          className='p-1 hover:bg-gray-100 rounded shrink-0 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent'
          aria-label='Navigate left'
          ref={el => {
            arrowButtonsRef.current.left = el
          }}
        >
          <ChevronLeft className='h-5 w-5' />
        </Button>
        <Button
          type='button'
          variant='ghost'
          onClick={() => handleNavigate('right')}
          disabled={!showRightArrow}
          className='p-1 hover:bg-gray-100 rounded shrink-0 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent'
          aria-label='Navigate right'
          ref={el => {
            arrowButtonsRef.current.right = el
          }}
        >
          <ChevronRight className='h-5 w-5' />
        </Button>
      </nav>

      <div
        className='fixed top-0 left-[-9999px] opacity-0 pointer-events-none'
        aria-hidden='true'
      >
        <div className='flex gap-4 pb-2'>
          {items.map((item, index) => {
            const count = counts ? counts[index] : undefined
            const label = getItemLabel ? getItemLabel(item) : item
            return (
              <button
                key={`measure-${item}`}
                ref={el => {
                  measureButtonsRef.current[index] = el
                }}
                className='px-4 py-2 whitespace-nowrap font-medium text-sm transition-colors'
                tabIndex={-1}
              >
                {label}
                {count !== undefined && (
                  <span className='text-xs text-gray-500 ml-2 bg-gray-100 border rounded-full p-1'>
                    {count}
                  </span>
                )}
              </button>
            )
          })}
        </div>
      </div>
    </>
  )
}
